import React from 'react';
import { GoalDepositData } from './AddGoalDepositFlow';
import { CURRENCY_SYMBOLS, WEEK_DAYS, FREQUENCY_LABELS } from './constants';
import { Icon } from '../Icon';
import './DepositConfirmation.css';

type Step = 'type' | 'amount' | 'frequency' | 'date' | 'schedule' | 'confirm';

interface DepositConfirmationProps {
  data: GoalDepositData;
  goalName: string;
  onConfirm: () => void;
  onEdit: (step: Step) => void;
  onBack: () => void;
  isSubmitting: boolean;
  currency: string;
}

export const DepositConfirmation: React.FC<DepositConfirmationProps> = ({
  data,
  goalName,
  onConfirm,
  onEdit,
  onBack,
  isSubmitting,
  currency
}) => {
  const currencySymbol = CURRENCY_SYMBOLS[currency] || currency;

  const getTypeLabel = () => {
    return data.type === 'onetime' ? 'Разовое пополнение' : 'Регулярное пополнение';
  };

  const getFrequencyLabel = () => {
    if (!data.frequency) return '';
    return FREQUENCY_LABELS[data.frequency] || data.frequency;
  };

  const getScheduleDetails = () => {
    if (data.type === 'onetime') return null;
    
    let details = '';
    
    if (data.frequency === 'weekly' && data.dayOfWeek !== undefined) {
      details = `Каждый ${WEEK_DAYS[data.dayOfWeek]}`;
    } else if (data.frequency === 'monthly' && data.dayOfMonth !== undefined) {
      details = `Каждое ${data.dayOfMonth} число месяца`;
    } else if (data.frequency === 'yearly' && data.dayAndMonth) {
      const monthName = new Date(2024, data.dayAndMonth.month - 1).toLocaleString('ru', { month: 'long' });
      details = `${data.dayAndMonth.day} ${monthName} каждого года`;
    } else if (data.frequency === 'daily') {
      details = 'Каждый день';
    }
    
    return details;
  };

  return (
    <div className="deposit-confirmation step-content">
      <div className="deposit-confirmation__content">
        <div className="deposit-confirmation__header">
          <button 
            className="deposit-confirmation__back" 
            onClick={onBack}
            disabled={isSubmitting}
          >
            <Icon name="chevron_left" size={20} />
          </button>
          <div className="deposit-confirmation__header-content">
            <h2 className="deposit-confirmation__title">Подтверждение</h2>
            <p className="deposit-confirmation__subtitle">
              Проверьте данные пополнения
            </p>
          </div>
        </div>

        <div className="deposit-confirmation__preview">
          <div className="deposit-preview-card">
            <div className="deposit-preview-card__goal-info">
              <div className="deposit-preview-card__icon">
                {data.type === 'onetime' ? '💰' : '🔄'}
              </div>
              <div className="deposit-preview-card__goal-name">{goalName}</div>
            </div>
            
            <div className="deposit-preview-card__type">{getTypeLabel()}</div>
            
            <div className="deposit-preview-card__amount">
              {(data.amount || 0).toLocaleString()} {currencySymbol}
            </div>
            
            {data.type === 'recurring' && (
              <>
                <div className="deposit-preview-card__frequency">
                  {getFrequencyLabel()}
                </div>
                {getScheduleDetails() && (
                  <div className="deposit-preview-card__schedule">
                    {getScheduleDetails()}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="deposit-confirmation__details">
          <button 
            className="deposit-detail-item" 
            onClick={() => onEdit('type')}
            disabled={isSubmitting}
          >
            <div className="deposit-detail-item__label">Тип</div>
            <div className="deposit-detail-item__value">{getTypeLabel()}</div>
            <div className="deposit-detail-item__edit">✎</div>
          </button>

          <button 
            className="deposit-detail-item" 
            onClick={() => onEdit('amount')}
            disabled={isSubmitting}
          >
            <div className="deposit-detail-item__label">Сумма</div>
            <div className="deposit-detail-item__value">
              {(data.amount || 0).toLocaleString()} {currencySymbol}
            </div>
            <div className="deposit-detail-item__edit">✎</div>
          </button>

          {data.type === 'recurring' && (
            <>
              <button 
                className="deposit-detail-item" 
                onClick={() => onEdit('frequency')}
                disabled={isSubmitting}
              >
                <div className="deposit-detail-item__label">Частота</div>
                <div className="deposit-detail-item__value">{getFrequencyLabel()}</div>
                <div className="deposit-detail-item__edit">✎</div>
              </button>

              {data.frequency !== 'daily' && (
                <button 
                  className="deposit-detail-item" 
                  onClick={() => onEdit('date')}
                  disabled={isSubmitting}
                >
                  <div className="deposit-detail-item__label">День</div>
                  <div className="deposit-detail-item__value">{getScheduleDetails()}</div>
                  <div className="deposit-detail-item__edit">✎</div>
                </button>
              )}

              <button 
                className="deposit-detail-item" 
                onClick={() => onEdit('schedule')}
                disabled={isSubmitting}
              >
                <div className="deposit-detail-item__label">Дата и время</div>
                <div className="deposit-detail-item__value">
                  {data.startDate && (() => {
                    // Парсим дату как локальную, без UTC конвертации
                    const [year, month, day] = data.startDate.split('-').map(Number);
                    const localDate = new Date(year, month - 1, day);
                    return localDate.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
                  })()}
                  {data.notificationTime && ` в ${data.notificationTime}`}
                </div>
                <div className="deposit-detail-item__edit">✎</div>
              </button>
            </>
          )}
        </div>
      </div>

      <div className="deposit-confirmation__footer">
        <button
          className="deposit-confirmation__submit"
          onClick={onConfirm}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Сохранение...' : data.type === 'onetime' ? 'Пополнить' : 'Сохранить'}
        </button>
      </div>
    </div>
  );
};

